import React from 'react';
import {
  MapPin,
  Phone,
  Globe,
  Zap,
  ArrowRight,
  Mail,
  CheckCircle2,
  Search as SearchIcon,
  HelpCircle,
  Clock3,
  Megaphone,
} from 'lucide-react';
import HoverTooltip from '../../common/HoverTooltip/HoverTooltip';
import Avatar from '../../common/Avatar';
import { Place, Person, EmailData, GoogleAdsSignal } from '../../../types/results.types';
import { toTitleCase } from '../../../utils/formatting';
import { placeHasCatchAllBusinessDomain } from '../../../utils/catchAllDetection';
import { readyCount, findableCount } from '../../../utils/rowActions';
import { getCategoryIcon, MatchEvidencePreview, hasMatchEvidence } from './MatchEvidenceTab';

function getSocialPlatform(url: string): { name: string; color: string; icon: React.ReactElement } {
  const lower = url.toLowerCase();
  if (lower.includes('facebook.com')) {
    return {
      name: 'Facebook',
      color: '#1877F2',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>,
    };
  }
  if (lower.includes('instagram.com')) {
    return {
      name: 'Instagram',
      color: '#E4405F',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>,
    };
  }
  if (lower.includes('linkedin.com')) {
    return {
      name: 'LinkedIn',
      color: '#0A66C2',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>,
    };
  }
  if (lower.includes('twitter.com') || lower.includes('x.com')) {
    return {
      name: 'X',
      color: '#000000',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>,
    };
  }
  if (lower.includes('youtube.com')) {
    return {
      name: 'YouTube',
      color: '#FF0000',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>,
    };
  }
  if (lower.includes('tiktok.com')) {
    return {
      name: 'TikTok',
      color: '#000000',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" /></svg>,
    };
  }
  if (lower.includes('pinterest.com')) {
    return {
      name: 'Pinterest',
      color: '#BD081C',
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>,
    };
  }
  return {
    name: 'Link',
    color: '#6B7280',
    icon: <Globe width={16} height={16} />,
  };
}

const websiteDomainLabel = (website: string): string => {
  try {
    return new URL(website).hostname.replace(/^www\./, '');
  } catch {
    return website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
  }
};

const formatAddress = (place: Place): string => {
  const parts: string[] = [];
  if (place.road) parts.push(place.road);
  if (place.city) parts.push(place.city);
  if (place.postcode) parts.push(place.postcode);
  if (place.country) parts.push(place.country);
  return parts.join(', ') || 'Address not available';
};

const AD_PIXELS = [
  { key: 'meta_pixel', label: 'Meta', hint: 'Meta Pixel code was found at the time of the website check.' },
  { key: 'tiktok_pixel', label: 'TikTok', hint: 'TikTok Pixel code was found at the time of the website check.' },
  { key: 'linkedin_insight', label: 'LinkedIn', hint: 'LinkedIn Insight Tag code was found at the time of the website check.' },
  { key: 'gtm', label: 'GTM', hint: 'Google Tag Manager code was found at the time of the website check.' },
] as const;

const SignalChip: React.FC<{ label: string; hint: string; on: boolean | null | undefined }> = ({ label, hint, on }) => {
  if (on === true) {
    return (
      <HoverTooltip content={hint}>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">
          {label} <span className="text-emerald-500">✓</span>
        </span>
      </HoverTooltip>
    );
  }

  return (
    <HoverTooltip content={hint}>
      <span className={`inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-semibold text-gray-400 opacity-70 ${on === false ? 'line-through' : ''}`}>
        {label}
      </span>
    </HoverTooltip>
  );
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode; aside?: React.ReactNode }> = ({ title, children, aside }) => (
  <section className="rounded-[22px] border border-[#EEE6E1] bg-white p-5 shadow-[0_10px_24px_rgba(45,49,66,0.04)]">
    <div className="mb-4 flex items-start justify-between gap-3">
      <h3 className="text-base font-bold tracking-tight text-[#2D3142]">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

const formatSignalDate = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
};

const signalAgeDays = (value?: string | null): number | null => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return null;
  return Math.max(0, Math.floor((Date.now() - timestamp) / 86_400_000));
};

const pluralize = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

const evidenceLabel = (evidenceTypes: string[]): string => {
  if (evidenceTypes.includes('conversion_request')) return 'Google Ads conversion request';
  if (evidenceTypes.includes('remarketing_request')) return 'Google Ads remarketing request';
  if (evidenceTypes.includes('aw_tag')) return 'Google Ads AW conversion tag';
  if (evidenceTypes.includes('legacy_html_match')) return 'Google Ads conversion tag';
  return 'Google Ads-specific website signal';
};

const AdvertisingSignalsCard: React.FC<{ signal: GoogleAdsSignal }> = ({ signal }) => {
  const detected = signal.status === 'detected';
  const notDetected = signal.status === 'not_detected';
  const referenceDate = detected
    ? signal.last_detected_at
    : notDetected
      ? signal.last_checked_at
      : signal.last_attempted_at;
  const formattedDate = formatSignalDate(referenceDate);
  const age = signalAgeDays(referenceDate);
  const foundOn = signal.detected_page_paths.length > 0
    ? `${signal.detected_page_paths.slice(0, 3).map(path => path === '/' ? 'Homepage' : path).join(', ')} • ${signal.pages_detected} of ${pluralize(signal.pages_checked, 'scanned page')}`
    : null;

  const dateChip = age != null ? (
    <span
      title={formattedDate ?? undefined}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${
        signal.freshness === 'stale'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-[#EAF2FB] text-[#1C5DA5]'
      }`}
    >
      <Clock3 className="h-2.5 w-2.5" aria-hidden="true" />
      {age === 0 ? 'Today' : `${pluralize(age, 'day')} ago`}
    </span>
  ) : (
    <span className="text-[#2D3142]">{formattedDate ?? 'Not available'}</span>
  );

  const asideBadge = (
    <span className="inline-flex items-center gap-1 rounded border border-[#8A3033]/20 bg-[#8A3033]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8A3033]">
      <Zap className="h-2.5 w-2.5" aria-hidden="true" /> Beta
    </span>
  );

  if (!detected) {
    const title = notDetected ? 'No Google Ads signal detected' : 'Check inconclusive';
    const tooltipCopy = notDetected
      ? 'No Google Ads-specific signal was found on the pages checked. This does not prove the company is not advertising. Some advertisers use server-side tagging or consent tools that hide signals from a page scan.'
      : "We couldn't reliably check this website for Google Ads signals. Evidence may be stale or the site couldn't be read on this pass. It can be refreshed during a future scheduled website scan.";

    return (
      <SectionCard title="Advertising signals" aside={asideBadge}>
        <div className="flex items-center gap-2 text-xs text-[#5A6072]">
          {notDetected ? <SearchIcon className="h-4 w-4 flex-none text-gray-400" /> : <HelpCircle className="h-4 w-4 flex-none text-amber-500" />}
          <span className="min-w-0">
            {title} · {pluralize(signal.pages_checked, 'page')} checked · {dateChip}
          </span>
          <HoverTooltip content={tooltipCopy}>
            <HelpCircle className="h-3.5 w-3.5 flex-none cursor-help text-gray-400" aria-hidden="true" />
          </HoverTooltip>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Advertising signals" aside={asideBadge}>
      <div className="flex items-start gap-3 rounded-2xl border border-[#D5E5F7] bg-[linear-gradient(180deg,#F3F8FE,#fff)] p-4">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#EAF2FB] text-[#2B6CB0]">
          <Megaphone className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-bold text-[#1C5DA5]">Google Ads tracking found</h4>
          <p className="mt-1 text-xs leading-5 text-[#5A6072]">
            A Google Ads conversion or remarketing signal was found on this website.
          </p>
        </div>
      </div>

      <p className="mb-2 mt-4 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400">Evidence</p>
      <dl className="space-y-2.5 text-xs">
        <div className="flex flex-col justify-between gap-1 sm:flex-row sm:gap-3">
          <dt className="text-gray-500">Signal type</dt>
          <dd className="font-semibold text-[#2D3142] sm:text-right">{evidenceLabel(signal.evidence_types)}</dd>
        </div>
        {foundOn && (
          <div className="flex flex-col justify-between gap-1 sm:flex-row sm:gap-3">
            <dt className="text-gray-500">Found on</dt>
            <dd className="break-words font-semibold text-[#2D3142] sm:text-right">{foundOn}</dd>
          </div>
        )}
        <div className="flex flex-col justify-between gap-1 sm:flex-row sm:gap-3">
          <dt className="text-gray-500">Source</dt>
          <dd className="font-semibold text-[#2D3142] sm:text-right">
            {signal.evidence_types.some(type => type.endsWith('_request'))
              ? 'Website HTML and request scan'
              : 'Website HTML tag scan'}
          </dd>
        </div>
        <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center sm:gap-3">
          <dt className="text-gray-500">Checked</dt>
          <dd className="inline-flex flex-wrap items-center gap-1.5 font-semibold sm:justify-end sm:text-right">
            {dateChip}
          </dd>
        </div>
      </dl>

      <p className="mt-4 border-t border-[#F1ECE6] pt-3 text-[11px] italic leading-4 text-gray-400">
        Website evidence only. It does not prove a campaign is live, its budget, or its performance.
      </p>
    </SectionCard>
  );
};

interface OverviewTabProps {
  place: Place;
  searchId?: number;
  shortLabels?: Record<string, string>;
  onViewMatchEvidence?: () => void;
  onViewPeople?: () => void;
}

const getBestEmail = (emails: EmailData[] | undefined): EmailData | undefined => {
  if (!emails || emails.length === 0) return undefined;
  return (
    emails.find(e => e.verification_status === 'GUARANTEED') ??
    emails.find(e => !e.is_catch_all) ??
    emails[0]
  );
};

const getPersonName = (person: Person): { firstName: string; lastName: string } => ({
  firstName: person.corrected_first_name ?? person.first_name,
  lastName: person.corrected_last_name ?? person.last_name ?? '',
});

const getPersonRole = (person: Person): string =>
  person.corrected_role ?? person.role;

const OverviewTab: React.FC<OverviewTabProps> = ({ place, searchId, shortLabels = {}, onViewMatchEvidence, onViewPeople }) => {
  const leadSignals = place.lead_signals;
  const rawPixels = leadSignals?.detected_pixels ?? place.detected_pixels;
  const detectedPixels = rawPixels ?? [];
  const pixelsWereScraped = rawPixels !== null && rawPixels !== undefined;

  const ssl = leadSignals?.has_ssl ?? place.has_ssl;
  const mobile = leadSignals?.is_mobile_ready ?? place.is_mobile_ready;
  const clickToCall = leadSignals?.has_click_to_call ?? place.has_click_to_call;
  const googleAdsSignal = leadSignals?.google_ads_signal;
  const hasSignalData = pixelsWereScraped || ssl != null || mobile != null || clickToCall != null;

  const pixelOn = (key: string): boolean | null => {
    if (!pixelsWereScraped) return null;
    return detectedPixels.includes(key);
  };

  const fit = place.website_fit;
  const matchEvidenceAvailable = hasMatchEvidence(place);

  const normalizeSocialUrl = (url: string): string =>
    url.trim().toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');

  const seenSocialUrls = new Set<string>();
  const socialMediaLinks = (place.website_scraped_data?.filter(item => item.data_type === 'SOCIAL_MEDIA') ?? [])
    .filter(item => {
      const key = normalizeSocialUrl(item.data);
      if (seenSocialUrls.has(key)) return false;
      seenSocialUrls.add(key);
      return true;
    });

  type CardKey = 'categories' | 'signals' | 'advertising' | 'evidence' | 'about' | 'social';
  const WEIGHTS: Record<CardKey, number> = { categories: 60, signals: 130, advertising: 260, evidence: 260, about: 80, social: 100 };
  const cardVisible: Record<CardKey, boolean> = {
    categories: !!(place.industries && place.industries.length > 0),
    signals: hasSignalData,
    advertising: !!googleAdsSignal,
    evidence: !!(matchEvidenceAvailable && onViewMatchEvidence),
    about: true,
    social: socialMediaLinks.length > 0,
  };
  const leftCards = new Set<CardKey>();
  const rightCards = new Set<CardKey>();
  let leftWeight = 160;
  let rightWeight = 0;
  if (cardVisible.signals) { leftCards.add('signals'); leftWeight += WEIGHTS.signals; }
  if (cardVisible.advertising) { rightCards.add('advertising'); rightWeight += WEIGHTS.advertising; }
  for (const key of ['categories', 'evidence', 'about', 'social'] as CardKey[]) {
    if (!cardVisible[key]) continue;
    if (leftWeight <= rightWeight) { leftCards.add(key); leftWeight += WEIGHTS[key]; }
    else { rightCards.add(key); rightWeight += WEIGHTS[key]; }
  }

  return (
    <div className="space-y-5">
      {fit && (
        <section className={`rounded-[22px] border p-5 shadow-[0_10px_24px_rgba(45,49,66,0.04)] ${
          fit.delivery_group === 'website_confirmed'
            ? 'border-emerald-200 bg-[linear-gradient(180deg,rgba(236,253,245,0.8),rgba(255,255,255,0.95))]'
            : 'border-amber-200 bg-[linear-gradient(180deg,rgba(255,247,237,0.8),rgba(255,255,255,0.95))]'
        }`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7B7280]">Why this prospect matched</p>
              <h3 className="mt-2 text-base font-bold leading-snug text-[#2D3142]">
                {fit.delivery_group === 'website_confirmed' ? 'Strong website match' : 'Partial website match'}
              </h3>
            </div>
            {fit.fit_score != null && (
              <span className="rounded-full bg-[#EAF8F1] px-3 py-1.5 text-xs font-semibold text-[#2D3142] shadow-sm">
                Website match {fit.fit_score}/100
              </span>
            )}
          </div>

          {fit.matched_offerings.length > 0 && (
            <div className="mt-3">
              <p className="mb-3 text-xs text-gray-600">
                {fit.matched_offerings.length} matching service{fit.matched_offerings.length !== 1 ? 's' : ''} found across {fit.pages_analyzed} website page{fit.pages_analyzed !== 1 ? 's' : ''}.
              </p>
              <div className="flex flex-wrap gap-2">
                {fit.matched_offerings.slice(0, 3).map(offering => {
                  const shortLabel = shortLabels[offering] ?? offering;
                  return (
                    <span
                      key={offering}
                      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-emerald-700"
                    >
                      <span className="text-emerald-500">{getCategoryIcon(shortLabel)}</span>
                      {shortLabel}
                    </span>
                  );
                })}
                {fit.matched_offerings.length > 3 && (
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
                    +{fit.matched_offerings.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {matchEvidenceAvailable && onViewMatchEvidence ? (
            <div className="mt-4">
              <button
                type="button"
                onClick={onViewMatchEvidence}
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#8A3033] underline underline-offset-4"
              >
                View match evidence
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-600">
              {fit.verdict === 'unknown'
                ? 'We could not access enough website content to verify these services.'
                : 'We did not find enough website evidence for the requested services.'}
            </p>
          )}
        </section>
      )}

      {place.people && place.people.length > 0 && (() => {
        const people = place.people!;
        const isReady = readyCount(place) > 0;
        const isCatchAll = !isReady && placeHasCatchAllBusinessDomain(place);
        const isFindable = !isReady && !isCatchAll && findableCount(place) > 0;
        const featuredPerson = people[0];
        const { firstName, lastName } = getPersonName(featuredPerson);
        const bestEmail = getBestEmail(featuredPerson.emails);

        return (
          <SectionCard
            title="Contact readiness"
            aside={
              isReady ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Ready to contact
                </span>
              ) : isCatchAll ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  Catch-all
                </span>
              ) : isFindable ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  Needs email discovery
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-500">
                  No email found
                </span>
              )
            }
          >
            <p className={`mb-3 ${isReady ? 'text-xs text-[#6B7280]' : 'text-base font-bold text-[#2D3142]'}`}>
              {people.length} decision maker{people.length !== 1 ? 's' : ''} found
            </p>
            <div className="flex items-center gap-3 rounded-xl border border-[#EEE6E1] bg-[#FFFEFD] px-4 py-3">
              <Avatar variant="person" firstName={firstName} lastName={lastName} size="md" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#2D3142]">
                  {toTitleCase(`${firstName} ${lastName}`.trim())}
                </p>
                <p className="text-xs text-[#6B7280]">{getPersonRole(featuredPerson)}</p>
              </div>
              {bestEmail ? (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 flex-shrink-0 text-[#6B7280]" />
                  <span className="text-sm text-[#2D3142]">{bestEmail.email}</span>
                  {bestEmail.verification_status === 'GUARANTEED' && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                  <Mail className="h-4 w-4 flex-shrink-0 text-[#6B7280]" />
                  <span>{isFindable ? 'No email found yet' : 'No confirmed email · auto-searched'}</span>
                </div>
              )}
            </div>
            {onViewPeople && (
              <div className={`mt-3 flex ${isFindable ? 'items-center justify-between gap-3' : 'justify-end'}`}>
                {isFindable && (
                  <p className="text-xs text-[#6B7280]">
                    Decision maker identified. Open People &amp; Emails to find their email.
                  </p>
                )}
                <button
                  type="button"
                  onClick={onViewPeople}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-[#8A3033] underline underline-offset-4"
                >
                  {isFindable ? 'Find email' : 'View people & emails'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </SectionCard>
        );
      })()}

      {(() => {
        const categoriesCard = place.industries && place.industries.length > 0 ? (
          <SectionCard key="categories" title="Categories">
            <div className="flex flex-wrap gap-2">
              {place.industries.slice(0, 5).map(industry => (
                <span key={industry.id} className="inline-flex items-center rounded-md bg-[#8A3033] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  {industry.name}
                </span>
              ))}
              {place.industries.length > 5 && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                  +{place.industries.length - 5} more
                </span>
              )}
            </div>
          </SectionCard>
        ) : null;

        const signalsCard = hasSignalData ? (
          <SectionCard
            key="signals"
            title="Website signals"
          >
            <div className="space-y-3">
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Web fundamentals</p>
                <div className="flex flex-wrap gap-2">
                  <SignalChip label="SSL" hint="HTTPS detected, a basic trust signal required for credible ad landing pages." on={ssl} />
                  <SignalChip label="Mobile ready" hint="Site appears mobile-optimized, critical for converting ad traffic from mobile users." on={mobile} />
                  <SignalChip label="Click-to-call" hint="Phone number configured with tap-to-call. They likely value direct inbound calls." on={clickToCall} />
                </div>
              </div>
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Tracking technologies</p>
                <div className="flex flex-wrap gap-2">
                  {AD_PIXELS.map(pixel => (
                    <SignalChip key={pixel.key} label={pixel.label} hint={pixel.hint} on={pixelOn(pixel.key)} />
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>
        ) : null;

        const advertisingCard = googleAdsSignal ? (
          <AdvertisingSignalsCard key="advertising" signal={googleAdsSignal} />
        ) : null;

        const evidenceCard = matchEvidenceAvailable && onViewMatchEvidence ? (
          <MatchEvidencePreview
            key="evidence"
            place={place}
            searchId={searchId}
            shortLabels={shortLabels}
            onViewAll={onViewMatchEvidence}
          />
        ) : null;

        const aboutCard = (
          <SectionCard key="about" title="About">
            {place.introduction
              ? <p className="text-sm leading-relaxed text-[#6B7280]">{place.introduction}</p>
              : <p className="text-sm italic text-gray-400">No description available</p>
            }
            {(place.book_visit || place.menu) && (
              <div className="mt-4 space-y-3">
                {place.book_visit && (
                  <div>
                    <h4 className="mb-2 text-sm font-bold tracking-tight text-[#2D3142]">Booking</h4>
                    {place.book_visit.startsWith('http') ? (
                      <a
                        href={place.book_visit}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-[#8A3033] px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 ease-in-out hover:bg-[#722629] active:scale-95"
                      >
                        Book Visit <Globe className="h-4 w-4" />
                      </a>
                    ) : (
                      <p className="text-sm text-[#6B7280]">{place.book_visit}</p>
                    )}
                  </div>
                )}
                {place.menu && (
                  <div>
                    <h4 className="mb-2 text-sm font-bold tracking-tight text-[#2D3142]">Menu</h4>
                    <p className="text-sm text-[#6B7280]">{place.menu}</p>
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        );

        const socialCard = socialMediaLinks.length > 0 ? (
          <SectionCard key="social" title="Social Media">
            <div className="flex flex-wrap gap-2">
              {socialMediaLinks.map((social, index) => {
                const { name, color, icon } = getSocialPlatform(social.data);
                return (
                  <a
                    key={index}
                    href={social.data}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={name}
                    className="flex items-center gap-1.5 rounded-lg border border-[#F9F5F5] bg-white px-3 py-1.5 text-sm font-medium shadow-sm transition-all duration-200 hover:shadow-md"
                    style={{ color }}
                  >
                    {icon}
                    <span className="text-[#2D3142]">{name}</span>
                  </a>
                );
              })}
            </div>
          </SectionCard>
        ) : null;

        const allCards: Record<CardKey, React.ReactNode> = {
          categories: categoriesCard,
          signals: signalsCard,
          advertising: advertisingCard,
          evidence: evidenceCard,
          about: aboutCard,
          social: socialCard,
        };

        return (
          <div className="grid gap-5 lg:grid-cols-[minmax(220px,0.85fr)_minmax(320px,1.35fr)]">
            <div className="space-y-5">
              <SectionCard title="Contact Information">
                <div className="space-y-4">
                  {(place.address || place.road || place.city) && (
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F9F5F5]">
                        <MapPin className="h-5 w-5 text-[#8A3033]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-[#6B7280]">Address</p>
                        <p className="mt-1 text-sm text-[#2D3142]">{toTitleCase(formatAddress(place))}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F9F5F5]">
                      <Phone className={`h-5 w-5 ${place.phone_number ? 'text-[#8A3033]' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wider text-[#6B7280]">Phone</p>
                      {place.phone_number ? (
                        <a href={`tel:${place.phone_number}`} className="mt-1 block text-sm font-medium text-[#2D3142] transition-colors duration-200 hover:text-[#1A1A1A]">
                          {place.phone_number}
                        </a>
                      ) : (
                        <p className="mt-1 text-sm text-gray-400">Not available</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F9F5F5]">
                      <Globe className={`h-5 w-5 ${place.website ? 'text-[#8A3033]' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wider text-[#6B7280]">Website</p>
                      {place.website ? (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <a
                            href={place.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-[#2D3142] transition-colors duration-200 hover:text-[#1A1A1A]"
                          >
                            {websiteDomainLabel(place.website)}
                          </a>
                          {placeHasCatchAllBusinessDomain(place) && (
                            <span
                              title="This domain accepts any address, so we can't confirm whether this inbox exists."
                              className="inline-flex items-center rounded border border-amber-400 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                            >
                              Catch-all domain
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-gray-400">Not available</p>
                      )}
                    </div>
                  </div>
                </div>
              </SectionCard>
              {(['categories', 'signals', 'advertising', 'evidence', 'about', 'social'] as CardKey[]).filter(k => leftCards.has(k)).map(k => allCards[k])}
            </div>
            <div className="space-y-5">
              {(['categories', 'signals', 'advertising', 'evidence', 'about', 'social'] as CardKey[]).filter(k => rightCards.has(k)).map(k => allCards[k])}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default OverviewTab;
